/**
 * Email Manager API Client
 */

import { apiClient as client } from './client';

// ==================== TYPES ====================

export interface EmailConfig {
    id: string;
    key: string;
    value: string;
    isSecret: boolean;
    description?: string;
}

export interface EmailTemplate {
    id: string;
    name: string;
    slug: string;
    subject: string;
    htmlContent: string;
    textContent?: string;
    variables: string;
    category: string;
    isActive: boolean;
    usageCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface EmailFlowStep {
    id: string;
    flowId: string;
    stepOrder: number;
    name: string;
    delayAmount: number;
    delayUnit: 'minutes' | 'hours' | 'days';
    subject: string;
    templateSlug?: string;
    htmlContent?: string;
    conditions: string;
    sentCount: number;
    openCount: number;
    clickCount: number;
    bounceCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface EmailFlow {
    id: string;
    name: string;
    slug?: string;
    description?: string;
    isSystem: boolean;
    triggerType: string;
    triggerConfig: string;
    status: 'draft' | 'active' | 'paused' | 'archived';
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    steps: EmailFlowStep[];
    stepsCount?: number;
    openRate?: string | number;
    clickRate?: string | number;
    createdAt: string;
    updatedAt: string;
    createdBy?: string;
}

export interface ProviderInfo {
    provider: string;
    fromEmail: string;
    currentProvider: string;
    isConfigured: boolean;
}

export interface EventType {
    type: string;
    label: string;
    category: string;
}

export interface EmailAnalytics {
    overview: {
        totalFlows: number;
        activeFlows: number;
        totalTemplates: number;
        totalSent: number;
        totalOpened: number;
        totalClicked: number;
        overallOpenRate: string;
        overallClickRate: string;
    };
    executions: Record<string, number>;
}

// ==================== API FUNCTIONS ====================

export const emailApi = {
    // Config
    getConfig: async (): Promise<EmailConfig[]> => {
        const res = await client.get('/admin/email/config');
        return res.data.data;
    },

    updateConfig: async (configs: Array<{ key: string; value: string; isSecret?: boolean; description?: string }>): Promise<void> => {
        await client.put('/admin/email/config', { configs });
    },

    getProviderInfo: async (): Promise<ProviderInfo> => {
        const res = await client.get('/admin/email/provider');
        return res.data.data;
    },

    testProvider: async (email: string): Promise<void> => {
        await client.post('/admin/email/provider/test', { email });
    },

    sendTestEmail: async (data: { to: string; subject: string; html: string }): Promise<void> => {
        await client.post('/admin/email/test-send', data);
    },

    // Templates
    listTemplates: async (filter?: { category?: string; isActive?: boolean }): Promise<EmailTemplate[]> => {
        const params = new URLSearchParams();
        if (filter?.category) params.append('category', filter.category);
        if (filter?.isActive !== undefined) params.append('isActive', String(filter.isActive));
        const res = await client.get(`/admin/email/templates?${params.toString()}`);
        return res.data.data;
    },

    createTemplate: async (data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
        const res = await client.post('/admin/email/templates', data);
        return res.data.data;
    },

    getTemplate: async (id: string): Promise<EmailTemplate> => {
        const res = await client.get(`/admin/email/templates/${id}`);
        return res.data.data;
    },

    updateTemplate: async (id: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
        const res = await client.put(`/admin/email/templates/${id}`, data);
        return res.data.data;
    },

    deleteTemplate: async (id: string): Promise<void> => {
        await client.delete(`/admin/email/templates/${id}`);
    },

    previewTemplate: async (id: string, testData: Record<string, string>): Promise<{ subject: string; html: string }> => {
        const res = await client.post(`/admin/email/templates/${id}/preview`, testData);
        return res.data.data;
    },

    // Flows
    listFlows: async (filter?: { status?: string; triggerType?: string }): Promise<EmailFlow[]> => {
        const params = new URLSearchParams();
        if (filter?.status) params.append('status', filter.status);
        if (filter?.triggerType) params.append('triggerType', filter.triggerType);
        const res = await client.get(`/admin/email/flows?${params.toString()}`);
        return res.data.data;
    },

    createFlow: async (data: { name: string; description?: string; triggerType: string; triggerConfig?: Record<string, unknown> }): Promise<EmailFlow> => {
        const res = await client.post('/admin/email/flows', data);
        return res.data.data;
    },

    getFlow: async (id: string): Promise<EmailFlow> => {
        const res = await client.get(`/admin/email/flows/${id}`);
        return res.data.data;
    },

    updateFlow: async (id: string, data: Partial<EmailFlow>): Promise<EmailFlow> => {
        const res = await client.put(`/admin/email/flows/${id}`, data);
        return res.data.data;
    },

    deleteFlow: async (id: string): Promise<void> => {
        await client.delete(`/admin/email/flows/${id}`);
    },

    activateFlow: async (id: string): Promise<EmailFlow> => {
        const res = await client.post(`/admin/email/flows/${id}/activate`);
        return res.data.data;
    },

    pauseFlow: async (id: string): Promise<EmailFlow> => {
        const res = await client.post(`/admin/email/flows/${id}/pause`);
        return res.data.data;
    },

    duplicateFlow: async (id: string): Promise<EmailFlow> => {
        const res = await client.post(`/admin/email/flows/${id}/duplicate`);
        return res.data.data;
    },

    getFlowAnalytics: async (id: string): Promise<unknown> => {
        const res = await client.get(`/admin/email/flows/${id}/analytics`);
        return res.data.data;
    },

    // Steps
    addStep: async (flowId: string, data: Partial<EmailFlowStep>): Promise<EmailFlowStep> => {
        const res = await client.post(`/admin/email/flows/${flowId}/steps`, data);
        return res.data.data;
    },

    updateStep: async (flowId: string, stepId: string, data: Partial<EmailFlowStep>): Promise<EmailFlowStep> => {
        const res = await client.put(`/admin/email/flows/${flowId}/steps/${stepId}`, data);
        return res.data.data;
    },

    deleteStep: async (flowId: string, stepId: string): Promise<void> => {
        await client.delete(`/admin/email/flows/${flowId}/steps/${stepId}`);
    },

    reorderSteps: async (flowId: string, stepIds: string[]): Promise<void> => {
        await client.post(`/admin/email/flows/${flowId}/steps/reorder`, { stepIds });
    },

    // Analytics
    getAnalytics: async (): Promise<EmailAnalytics> => {
        const res = await client.get('/admin/email/analytics');
        return res.data.data;
    },

    // Event Types
    getEventTypes: async (): Promise<EventType[]> => {
        const res = await client.get('/admin/email/event-types');
        return res.data.data;
    },

    // Process scheduled emails
    processEmails: async (): Promise<{ processed: number }> => {
        const res = await client.post('/admin/email/process');
        return res.data.data;
    },

    // Unsubscribe Management
    listUnsubscribed: async (page = 1, limit = 50): Promise<{
        data: Array<{ id: string; email: string; reason?: string; createdAt: string }>;
        pagination: { page: number; limit: number; total: number; pages: number };
    }> => {
        const res = await client.get(`/admin/email/unsubscribed?page=${page}&limit=${limit}`);
        return res.data;
    },

    resubscribe: async (email: string): Promise<void> => {
        await client.post('/admin/email/resubscribe', { email });
    },

    // Failed Executions
    listFailedExecutions: async (page = 1, limit = 50, flowId?: string): Promise<{
        data: Array<{
            id: string;
            email: string;
            flowName: string;
            stepName: string;
            errorMessage?: string;
            failedAt?: string;
            scheduledFor: string;
            createdAt: string;
        }>;
        pagination: { page: number; limit: number; total: number; pages: number };
    }> => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (flowId) params.append('flowId', flowId);
        const res = await client.get(`/admin/email/failed-executions?${params.toString()}`);
        return res.data;
    },

    retryExecution: async (executionId: string): Promise<void> => {
        await client.post(`/admin/email/failed-executions/${executionId}/retry`);
    },

    // Reset email templates and flows to defaults
    resetDefaults: async (): Promise<void> => {
        await client.post('/admin/email/reset-defaults');
    }
};
